// @flow
import moment from 'moment';
import {fromJS, List, type Map} from 'immutable';
import {Store} from 'client/framework';
import AssignmentDrilldownActions from './AssignmentDrilldownActions';
import sidebarMenuContainerActions from 'client/generic/SidebarMenuContainer/SidebarMenuContainerActions';
import {
  mandarkEndpoint,
  type QueryBuilder
} from '@albert-io/json-api-framework/request/builder/legacy';
// @todo: Upgrade the rest of these to use queryBuilder and resource models
import {
  getAssignmentStatistics,
  deleteAssignment
} from 'framework/MandarkAPI/Assignments/Assignments';
/* eslint-disable import/order,import/no-cycle */
import classroomStore from 'client/Classrooms/Classroom/ClassroomV2.store';
import studentsStore from './Students/StudentsStore';
import questionsStore from './Questions/QuestionsStore';
import stats, {getStatusNameByAccuracy} from 'lib/StatsUtil';
import sessionStore from 'client/Session/SessionStore';

import type {AssignmentModelV3} from 'resources/augmented/Assignment/AssignmentModel.v3';
import type {QuestionSet as QuestionSetModelV1} from 'resources/GeneratedModels/QuestionSet/QuestionSetModel.v1';
import type {StudentModel} from 'resources/Student/Student.model';

export class AssignmentDrilldownStore extends Store {
  constructor(name: string) {
    super(name);

    this.initialData = fromJS({
      classroomId: undefined,
      assignmentId: undefined,
      deleteAssignmentPromise: null
    });

    this.setInitialData(this.initialData);
    this.handle(AssignmentDrilldownActions.DELETE_ASSIGNMENT, this._deleteAssignment);
    this.handle(AssignmentDrilldownActions.RESET_STORE, this._resetStore);
    this.handle(
      AssignmentDrilldownActions.INITIALIZE_ASSIGNMENT_DRILLDOWN,
      this._initializeAssignmentDrilldown
    );
    this.handle(sidebarMenuContainerActions.SET_ACTIVE_ITEM, this._resetStore);
  }

  _initializeAssignmentDrilldown(initialData: Map<string, *>) {
    this.writeData((state) => {
      return state
        .set('classroomId', initialData.get('classroomId'))
        .set('assignmentId', initialData.get('assignmentId'));
    });
  }

  // Returns promise to the caller, to be handled directly in the react component.
  async _deleteAssignment() {
    this.writeData(
      'deleteAssignmentPromise',
      deleteAssignment(this._makeDeleteAssignmentQuery()).getPromise()
    );
  }

  getDeleteAssignmentPromise() {
    return this.readData('deleteAssignmentPromise');
  }

  _makeAssignmentQuery(): QueryBuilder {
    const assignmentId = this.readData('assignmentId');
    const classroomId = this.readData('classroomId');
    return mandarkEndpoint(['assignments_v3', assignmentId])
      .include('question_sets_v1.questions_v1,students_v2,classrooms_v1')
      .withMeta('assignment_v3,student_v2')
      .filter({
        included: {
          classrooms_v1: {
            id: classroomId
          },
          students_v2: {
            classrooms_v1: classroomId
          }
        }
      })
      .meta({
        context: {
          assignment: {
            id: assignmentId,
            stats: true
          },
          classroom: {
            id: classroomId
          }
        }
      })
      .customQuery({
        meta: {
          question_v1: {
            labels: true
          }
        }
      })
      .customQuery(
        {
          meta: {
            teacher_id: sessionStore.getUserId()
          }
        },
        sessionStore.getUserId()
      );
  }

  _makeDeleteAssignmentQuery() {
    return {
      assignmentId: this.readData('assignmentId')
    };
  }

  _makeStudentInsightsQuery() {
    return {
      studentId: this.readData('activeStudentId'),
      classroomId: this.readData('classroomId'),
      assignmentId: this.readData('assignmentId')
    };
  }

  _makeAssignmentStatisticsQuery() {
    return {
      classroomId: this.readData('classroomId'),
      assignmentId: this.readData('assignmentId')
    };
  }

  generateQuestionCSVData() {
    /*
      Generates a CSV representation of things in the questions table.

      Column headings are manually aligned in the body of our reduce.
    */
    const headings = [
      'Question Name',
      'Average Duration (seconds)',
      'Student Answers',
      'Students Missing Answers',
      'Correct Students',
      'Incorrect Students'
    ];

    const questionTableData = questionsStore.getQuestionTableData();
    return headings.join(',').concat(
      '\n',
      /* eslint-disable-next-line no-shadow */
      questionTableData.reduce((acc, stats) => {
        // concat the comma-joined list to our accumulator string w/ newline at the EOL
        return acc.concat(
          [
            `"${stats.get('questionName')}"`,
            stats.get('avgDuration') / 1000, // ms to s
            stats.get('studentsThatHaveAnswered'),
            stats.get('studentsThatHaveNotAnswered'),
            stats.get('studentsThatHaveAnsweredCorrectly'),
            stats.get('studentsThatHaveAnsweredIncorrectly')
          ].join(','),
          '\n'
        );
      }, '')
    );
  }

  generateStudentCSVData() {
    /*
      We're constructing a CSV file here using the output of the student stats query correlated against student
      data retrieved via the top level assignment detail query.

      Headers are not directly linked to what stats data comes back, we manually align the data to a
      list position here ourselves.
    */
    const headings = [
      'Student Name',
      'Student Email',
      'Submission Status',
      'Total Questions Answered',
      'Questions Answered Incorrectly',
      'Questions Answered Correctly',
      'Accuracy',
      'Grade',
      'Status'
    ];

    const studentTableData = studentsStore.getStudentTableData();
    return headings.join(',').concat(
      '\n',
      /* eslint-disable-next-line no-shadow */
      studentTableData.reduce((acc, stats, studentId) => {
        const hasStarted = stats.get('questionsAnswered') > 0;
        let submissionStatus;
        if (!hasStarted) {
          submissionStatus = 'Not Started';
        } else if (!stats.get('isComplete')) {
          submissionStatus = 'In Progress';
        } else {
          submissionStatus = stats.get('isLate') ? 'Late' : 'On Time';
        }

        const accuracy = stats.get('accuracy') > 0 ? stats.get('accuracy') : null;
        const grade = stats.get('correctPercentage');
        const student = this.getStudentById(studentId);

        // concat the joined array into our accumulator - then pop a newline on the end.
        return acc
          .concat(
            [
              student ? student.getFullName() : '',
              student ? student.getEmail() : '',
              submissionStatus,
              stats.get('questionsAnswered'),
              stats.get('incorrectAnswers'),
              stats.get('correctAnswers'),
              accuracy,
              grade,
              getStatusNameByAccuracy(grade, hasStarted)
            ].join(',')
          )
          .concat('\n');
      }, '')
    );
  }

  _resetStore() {
    this.writeData(this.initialData);
  }

  getAssignmentName(): string {
    return this.getActiveAssignment().getName();
  }

  getAssignmentId(): string {
    return this.getActiveAssignment().getId();
  }

  getCompletionDateTime(classroomId: string): moment {
    return this.getActiveAssignment().getDueDateFromSettings({classroomId});
  }

  getActiveAssignmentClassName(): string {
    return classroomStore.getActiveClassroom().getName();
  }

  getActiveAssignmentStudents(): List<StudentModel> {
    return this.getActiveAssignment().getStudents();
  }

  getActiveAssignmentQuestions(): List<*> {
    return this.getActiveAssignment().getSortedQuestions();
  }

  getActiveAssignmentQuestionTitle(questionId): string {
    return this.getQuestionSetForActiveQuestion(questionId).getName();
  }

  getActiveAssignmentQuestionDifficulty(questionId: string): number {
    return this.getQuestionSetForActiveQuestion(questionId).getDifficulty();
  }

  getQuestionSetForActiveQuestion(questionId): QuestionSetModelV1 {
    return this.getActiveAssignment().getQuestionSetForActiveQuestion(questionId);
  }

  getSlugForQuestion(questionId: string): string {
    return this.getQuestionSetForActiveQuestion(questionId).getSlugId();
  }

  getQuestionById(questionId) {
    return this.getActiveAssignment()
      .getSortedQuestions()
      .find((question) => question.getId() === questionId);
  }

  getActiveAssignmentDueDate(classroomId: string): moment {
    return this.getActiveAssignment().getDueDateFromSettings({classroomId});
  }

  getActiveAssignment(): AssignmentModelV3 {
    return this._makeAssignmentQuery().getResource();
  }

  hasActiveAssignment(): boolean {
    return this._makeAssignmentQuery().isResourcePopulated();
  }

  /*
    Assignment top level statistics
  */
  getAssignmentStats() {
    return getAssignmentStatistics(this._makeAssignmentStatisticsQuery());
  }

  hasAssignmentStats() {
    return getAssignmentStatistics(this._makeAssignmentStatisticsQuery()).hasReceivedData();
  }

  _getActiveAssignmentStatsInterface() {
    return this.getAssignmentStats().interface;
  }

  getActiveAssignmentStatsStudents() {
    return this._getActiveAssignmentStatsInterface().getStudentsInAssignmentAndClassroom();
  }

  getActiveAssignmentStatsStudentsAllClasses() {
    return this._getActiveAssignmentStatsInterface().getStudentsInAssignment();
  }

  getActiveAssignmentStatsClasses() {
    return this._getActiveAssignmentStatsInterface().getClassesInAssignment();
  }

  getActiveAssignmentStatsFinishedStudents() {
    return this._getActiveAssignmentStatsInterface().getFinishedStudents();
  }

  getActiveAssignmentFinishedStudents(): List<StudentModel> {
    return this.getActiveAssignment().getFinishedStudents();
  }

  getQuestionsAnswered() {
    return this._getActiveAssignmentStatsInterface().getQuestionsAnswered();
  }

  getQuestionsCorrectlyAnswered() {
    return this._getActiveAssignmentStatsInterface().getQuestionsCorrectlyAnswered();
  }

  getTotalQuestions() {
    return this._getActiveAssignmentStatsInterface().getTotalQuestions();
  }

  /**
   * @todo: Update this to read from the "average_accuracy" metadata field sometime after mandark-5.6.0
   * This can likely be done along with a wholesale port/update to Autogen models
   */
  getActiveAssignmentStatsAverageGrade() {
    const questionsCorrectlyAnswered = this.getQuestionsCorrectlyAnswered();
    const assignmentTotalQuestions = this.getTotalQuestions();
    const numberOfStudents = this.getActiveAssignmentStatsStudents();
    const totalQuestionsForAllStudents = assignmentTotalQuestions * numberOfStudents;

    return stats.percentage(questionsCorrectlyAnswered, totalQuestionsForAllStudents);
  }

  getActiveAssignmentStatsProgress(): number {
    return stats.percentage(
      this.getActiveAssignmentFinishedStudents().size,
      this.getActiveAssignmentStatsStudents()
    );
  }

  getStudentById(studentId: string): StudentModel {
    return this.getActiveAssignmentStudents().find((student) => {
      return student.getId() === studentId;
    });
  }
}

export default new AssignmentDrilldownStore('AssignmentDrilldownStore');
